/**
 * The Care Delivery AI chat sidebar.
 *
 * Per concept.html: a ~360–420px right-hand panel with message bubbles, an
 * attach+send composer, and the clinical-safety disclaimer. It is multi-tab and
 * thread-resumable (auto-named + renamable threads via {@link useChatTabs}),
 * streams turns over SSE ({@link useChatStream} inside the tabs hook), and
 * carries a per-patient notification icon (count badge) that, on click, opens
 * the originating ingestion-review thread (reusing its tab if already open) —
 * never replacing the active tab. When there are no pending reviews the bell is
 * disabled with an explanatory tooltip.
 *
 * This sidebar now lives at the **app-shell level** ({@link AppContent}), not
 * inside the Blueprint tab — it is rendered once, beside all tab content, and
 * never unmounts on a tab switch (so the open thread, draft, and in-flight
 * stream survive). It is collapsible (default expanded; preference persisted via
 * {@link useChatCollapse}); when collapsed it renders a thin rail with an expand
 * affordance and the tab content reclaims the full width. No message content is
 * logged (HIPAA).
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useChatTabs, type ChatTab } from "../../hooks/useChatTabs";
import { usePendingReviews } from "../../hooks/usePendingReviews";
import { useDocumentLibrary } from "../../hooks/useDocumentLibrary";
import { findPendingReviewThread } from "./pendingReviewThread";
import { useReviewControl } from "../ReviewControlContext";
import { Markdown } from "./Markdown";
import { extractCitationsFromText } from "./citationResolve";
import apiUtils, { isApiError } from "../../apiUtils";
import type { ChatMessage, IngestionRun } from "../../types";
import ProposalDiffReview from "../documents/ProposalDiffReview";
import { THINKING_MODES, type ThinkingMode } from "./thinkingModes";

const DISCLAIMER =
  "Agent can make mistakes. Verify clinical data against schema.";

const INGESTION_RUNS_ENDPOINT = "/api/ingestion/runs";

/**
 * Fetch the most recent pending ingestion run for a patient, if any.
 *
 * The bell routes transcript-origin runs to the full-size ReviewPanel
 * (via ReviewControlContext) rather than re-opening the chat thread. For
 * chat-origin runs (or when origin is unknown) the original thread-reopen
 * path is used.
 *
 * @param patientId - The active patient id.
 * @returns The most recent pending run, or null.
 */
async function fetchMostRecentPendingRun(
  patientId: string,
): Promise<IngestionRun | null> {
  const resp = await apiUtils.fetchData<IngestionRun[]>(
    `${INGESTION_RUNS_ENDPOINT}?patient_id=${encodeURIComponent(patientId)}`,
  );
  if (isApiError(resp) || resp.length === 0) return null;
  return resp[0];
}

/**
 * Strip `[[ENT-…]]` entity-reference wrappers from a message, showing the inner
 * label as plain English.
 *
 * Backend summaries are moving to plain names, but a defensive render-time
 * sanitizer guarantees the chat never shows a raw `ENT-…` id or `[[ ]]`
 * brackets even on older/partial streams. The wrapper may carry a piped label
 * (`[[ENT-…|Karen Johnson]]` → "Karen Johnson"); otherwise the inner token is
 * de-prefixed and humanised (`ENT-p-789-karen-johnson` → "karen johnson").
 *
 * A `[[src:...]]` grounding citation is a DIFFERENT token family (rendered by
 * {@link Markdown} as a proper citation marker, never as plain text) and must
 * pass through untouched — matching it here would destructively mangle it
 * (`[-_]+` → spaces turns the id into unreadable garbage) before Markdown ever
 * gets a chance to parse it into a clean citation.
 *
 * @param text - The raw message text.
 * @returns The text with every `[[…]]` entity token replaced by plain English.
 */
function sanitizeEntityTokens(text: string): string {
  return text.replace(/\[\[([^\]]+)\]\]/g, (match, inner: string) => {
    if (/^src:/i.test(inner)) return match;
    const piped = inner.indexOf("|");
    if (piped !== -1) return inner.slice(piped + 1).trim();
    // No explicit label: drop a leading ENT- prefix and the patient-id segment,
    // then turn the remaining hyphenated id into spaced words.
    const deEnt = inner.replace(/^ENT-/i, "");
    const words = deEnt
      .replace(/^p-\d+-/i, "")
      .replace(/[-_]+/g, " ")
      .trim();
    return words === "" ? inner : words;
  });
}

/**
 * Vertical dropdown listing every open/saved thread, with per-thread rename and
 * delete controls. Selecting a thread switches to it. A fixed "New chat" button
 * sits beside the dropdown trigger so it stays reachable with many threads.
 */
function ThreadMenu({
  tabs,
  activeTabId,
  onSelect,
  onNew,
  onRename,
  onDelete,
}: {
  tabs: ChatTab[];
  activeTabId: string;
  onSelect: (tabId: string) => void;
  onNew: () => void;
  onRename: (tabId: string) => void;
  onDelete: (tabId: string) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const activeTab = tabs.find((t) => t.tabId === activeTabId);

  // Close the menu on an outside click so it behaves like a real dropdown.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const choose = (tabId: string): void => {
    onSelect(tabId);
    setOpen(false);
  };

  return (
    <div
      className="flex items-center gap-2 px-3 py-2 border-b border-slate-200"
      ref={containerRef}
    >
      <div className="relative flex-1 min-w-0">
        <button
          type="button"
          data-testid="thread-menu-trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label="Open conversations"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded border border-slate-200 bg-slate-50 text-xs font-medium text-slate-700 hover:bg-slate-100"
        >
          <span className="truncate">{activeTab?.title ?? "Conversation"}</span>
          <span aria-hidden="true" className="text-slate-400">
            ▾
          </span>
        </button>
        {open && (
          <ul
            data-testid="thread-menu"
            role="listbox"
            className="absolute z-10 left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded border border-slate-200 bg-white shadow-lg py-1"
          >
            {tabs.map((tab) => (
              <li
                key={tab.tabId}
                data-testid="thread-option"
                role="option"
                aria-selected={tab.tabId === activeTabId}
                className={`group flex items-center gap-1 px-2 py-1.5 text-xs ${
                  tab.tabId === activeTabId
                    ? "bg-slate-100 text-slate-900"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                <button
                  type="button"
                  onClick={() => choose(tab.tabId)}
                  className="flex-1 min-w-0 truncate text-left font-medium"
                >
                  {tab.title}
                </button>
                <button
                  type="button"
                  onClick={() => onRename(tab.tabId)}
                  aria-label={`Rename ${tab.title}`}
                  className="text-slate-400 hover:text-slate-700 px-1"
                >
                  ✎
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(tab.tabId)}
                  aria-label={`Delete ${tab.title}`}
                  className="text-slate-400 hover:text-rose-600 px-1"
                >
                  🗑
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <button
        type="button"
        onClick={onNew}
        aria-label="New chat"
        className="shrink-0 px-2.5 py-1.5 rounded bg-slate-800 text-white hover:bg-slate-700 text-sm leading-none"
      >
        +
      </button>
    </div>
  );
}

/** One message bubble (user right-aligned, assistant left-aligned). */
function Bubble({
  message,
  sourceTitles,
}: {
  message: ChatMessage;
  sourceTitles?: Record<string, string>;
}): React.ReactElement {
  // Defensive: never surface a raw `[[ENT-…]]` token (see sanitizeEntityTokens).
  const content = sanitizeEntityTokens(message.content);
  if (message.role === "user") {
    return (
      <div className="flex flex-col items-end">
        <div
          data-testid="user-message"
          className="bg-slate-100 text-slate-800 rounded-2xl px-4 py-2.5 max-w-[85%] text-[15px] whitespace-pre-wrap"
        >
          {content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3">
      <div
        className="w-8 h-8 rounded-full bg-slate-800 shrink-0 mt-1"
        aria-hidden="true"
      />
      <div className="text-slate-800 text-[15px] leading-relaxed space-y-2 [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:space-y-1">
        <Markdown
          source={content}
          sourceTitles={sourceTitles}
          citations={message.citations}
        />
      </div>
    </div>
  );
}

/**
 * The message list for the active tab, including the in-flight stream and the
 * chat-origin inline proposal review.
 *
 * The inline review (`origin=chat`, usually a single change) renders INSIDE this
 * scrollable area as the last item before the composer, so it scrolls *with* the
 * conversation and is bounded by the sidebar/page height (#4) — rather than as an
 * uncapped sibling below the list. Larger transcript ingestions open the
 * full-size ReviewPanel instead.
 */
function MessageList({
  messages,
  streaming,
  streamedText,
  pendingRunId,
  onResolved,
  sourceTitles,
}: {
  messages: ChatMessage[];
  streaming: boolean;
  streamedText: string;
  pendingRunId: string | null;
  onResolved: () => void;
  sourceTitles?: Record<string, string>;
}): React.ReactElement {
  return (
    <div
      data-testid="chat-message-list"
      className="flex-1 min-h-0 overflow-y-auto p-5 space-y-6"
    >
      {messages.map((message, index) => (
        <Bubble
          key={message.id || `local-${index}`}
          message={message}
          sourceTitles={sourceTitles}
        />
      ))}
      {streaming && (
        <div className="flex items-start gap-3" data-testid="streaming-bubble">
          <div
            className="w-8 h-8 rounded-full bg-slate-800 shrink-0 mt-1"
            aria-hidden="true"
          />
          <div className="text-slate-800 text-[15px] leading-relaxed space-y-2 [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:space-y-1">
            {streamedText ? (
              <Markdown
                source={sanitizeEntityTokens(streamedText)}
                sourceTitles={sourceTitles}
                citations={extractCitationsFromText(streamedText)}
              />
            ) : (
              "…"
            )}
          </div>
        </div>
      )}
      {pendingRunId !== null && (
        // origin="chat" reviews render UNCAPPED inline — no max-h cap that would
        // clip the diff; the surrounding list scrolls (#4/#6).
        <div
          data-testid="inline-review-container"
          className="rounded-lg border border-slate-200 bg-slate-50 p-4"
        >
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            Proposed card changes
          </div>
          <ProposalDiffReview runId={pendingRunId} onResolved={onResolved} />
        </div>
      )}
    </div>
  );
}

/** The attach + text + send composer with thinking mode selector. */
function Composer({
  disabled,
  onSend,
}: {
  disabled: boolean;
  onSend: (text: string, thinkingBudget: number) => void;
}): React.ReactElement {
  const [value, setValue] = useState("");
  const [thinkingMode, setThinkingMode] = useState<ThinkingMode>("fast");

  const selectedBudget =
    THINKING_MODES.find((m) => m.id === thinkingMode)?.budget ?? 0;

  const submit = (): void => {
    const trimmed = value.trim();
    if (trimmed === "" || disabled) return;
    onSend(trimmed, selectedBudget);
    setValue("");
  };

  return (
    <div className="p-5 border-t border-slate-200 bg-white shrink-0">
      <div className="flex items-center justify-between mb-2">
        <div
          data-testid="thinking-mode-selector"
          role="radiogroup"
          aria-label="Thinking mode"
          className="flex items-center gap-1 bg-slate-100 p-0.5 rounded-full border border-slate-200 text-xs"
        >
          {THINKING_MODES.map((mode) => {
            const active = mode.id === thinkingMode;
            return (
              <button
                key={mode.id}
                type="button"
                role="radio"
                aria-checked={active}
                data-testid={`thinking-mode-${mode.id}`}
                title={mode.tooltip}
                onClick={() => setThinkingMode(mode.id)}
                className={`px-2.5 py-0.5 rounded-full font-medium transition-colors text-xs flex items-center gap-1 ${
                  active
                    ? "bg-white text-slate-900 shadow-sm font-semibold"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                <span>{mode.label}</span>
                <span className="text-[10px] opacity-60 font-mono">
                  {mode.ttft}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="relative flex items-center bg-slate-100 rounded-3xl border border-slate-200 px-4 py-2">
        <button
          type="button"
          aria-label="Attach file"
          className="text-slate-400 hover:text-slate-600 mr-2"
        >
          📎
        </button>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Message Care Delivery AI..."
          aria-label="Message Care Delivery AI"
          className="w-full bg-transparent border-none focus:outline-none text-[15px] text-slate-800 placeholder-slate-400"
        />
        <button
          type="button"
          onClick={submit}
          disabled={disabled}
          aria-label="Send message"
          className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center ml-2 hover:bg-slate-700 disabled:opacity-40 shrink-0"
        >
          ↑
        </button>
      </div>
      <div className="text-center mt-3">
        <span className="text-[11px] text-slate-400 font-medium">
          {DISCLAIMER}
        </span>
      </div>
    </div>
  );
}

/** Props for {@link CareDeliveryChat}. */
interface CareDeliveryChatProps {
  /** The active patient id the chat is scoped to. */
  patientId: string;
  /**
   * Invoked after a chat turn finishes streaming. The Care Delivery AI commits
   * Blueprint edits (via the confirmation box), so the active view refetches on
   * this signal to surface the newly-committed content without a manual reload.
   */
  onTurnComplete?: () => void;
  /** Whether the sidebar is collapsed to its rail (default expanded). */
  collapsed?: boolean;
  /** Toggle the collapsed/expanded state (persisted by the shell). */
  onToggleCollapse?: () => void;
  /**
   * Register the chat's imperative "open pending review" handler with the shell
   * so descendants (e.g. the Sources subtab after an auto-ingest) can surface
   * the pending-review thread inline without prop-drilling.
   */
  onRegisterControl?: (openPendingReview: () => void) => void;
  /** Optional source-title lookup { [id]: title } for resolving inline citations. */
  sourceTitles?: Record<string, string>;
}

/**
 * Render the Care Delivery AI sidebar.
 *
 * @param props - The active patient id, optional turn-complete and collapse
 *   callbacks, collapsed state, and optional source-title lookup.
 * @returns The chat sidebar element.
 */
export const CareDeliveryChat: React.FC<CareDeliveryChatProps> = ({
  patientId,
  onTurnComplete,
  collapsed = false,
  onToggleCollapse,
  onRegisterControl,
  sourceTitles,
}) => {
  const tabs = useChatTabs(patientId);
  const { refresh } = usePendingReviews(patientId);
  const reviewControl = useReviewControl();
  const { documents: sourceDocs } = useDocumentLibrary(patientId, "source");

  const activeSourceTitles = useMemo(() => {
    if (sourceTitles) return sourceTitles;
    const map: Record<string, string> = {};
    for (const doc of sourceDocs) {
      map[doc.id] = doc.title;
    }
    return map;
  }, [sourceTitles, sourceDocs]);

  // The tab currently being renamed (null when no rename is in progress).
  const [renamingTabId, setRenamingTabId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  const activeTab = tabs.tabs.find((t) => t.tabId === tabs.activeTabId);

  // reopenInNewTab is stable (useCallback([])); tabs.refresh likewise. The
  // resolver is module-level. So this handler is stable across renders, letting
  // it register cleanly with the shell control channel.
  const { reopenInNewTab } = tabs;
  const handleNotificationClick = useCallback(async (): Promise<void> => {
    // For a transcript-origin pending run, route to the full-size ReviewPanel
    // (not the chat thread). For chat-origin or unknown origin, fall through to
    // the original thread-reopen path.
    const run = await fetchMostRecentPendingRun(patientId);
    if (run !== null && run.origin !== "chat") {
      reviewControl.openReview(run);
      refresh();
      return;
    }
    const threadId = await findPendingReviewThread(patientId);
    // reopenInNewTab reuses the thread's existing tab if already open, so a
    // repeated bell click refreshes that tab rather than duplicating it.
    if (threadId) await reopenInNewTab(threadId);
    refresh();
  }, [patientId, reopenInNewTab, refresh, reviewControl]);

  // Register the imperative "open pending review" action with the shell so a
  // descendant (auto-ingest in Sources) can surface the review thread inline.
  useEffect(() => {
    onRegisterControl?.(() => void handleNotificationClick());
  }, [onRegisterControl, handleNotificationClick]);

  const handleSend = (text: string, thinkingBudget: number = 0): void => {
    // The turn streams to completion, then mutations/proposals are reflected
    // by refetching the Blueprint (signalled via onTurnComplete).
    void tabs.send(patientId, text, thinkingBudget).then((outcome) => {
      if (outcome?.hasMutation) {
        onTurnComplete?.();
      }
    });
  };

  const beginRename = (tabId: string): void => {
    const tab = tabs.tabs.find((t) => t.tabId === tabId);
    setRenameValue(tab?.title ?? "");
    setRenamingTabId(tabId);
  };

  // Only rendered (and thus only callable) while a rename is in progress, so
  // `renamingTabId` is always a real tab id here.
  const commitRename = (renamingTabId: string): void => {
    void tabs.rename(renameValue.trim() || "Conversation", renamingTabId);
    setRenamingTabId(null);
  };

  const handleDelete = (tabId: string): void => {
    if (renamingTabId === tabId) setRenamingTabId(null);
    void tabs.deleteThread(tabId);
  };

  const pendingRunId =
    activeTab?.messages.find((m) => m.ingestion_run_id !== null)
      ?.ingestion_run_id ?? null;

  // Collapsed: render a thin rail with an expand affordance + the pending badge.
  // The chat's hooks (threads, stream, pending count) stay mounted so the open
  // thread, draft, and in-flight stream survive collapsing.
  if (collapsed) {
    return (
      <aside
        data-testid="chat-collapsed-rail"
        className="w-10 flex-shrink-0 bg-white border-l border-slate-200 flex flex-col items-center py-4 gap-4 h-full"
      >
        <button
          type="button"
          onClick={onToggleCollapse}
          aria-label="Expand Care Delivery AI"
          title="Expand Care Delivery AI"
          className="text-slate-500 hover:text-slate-800 text-lg"
        >
          ◀
        </button>
        <span
          className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 [writing-mode:vertical-rl]"
          aria-hidden="true"
        >
          Care Delivery AI
        </span>
      </aside>
    );
  }

  return (
    <aside
      data-testid="chat-sidebar"
      className="w-[360px] 2xl:w-[420px] flex-shrink-0 bg-white border-l border-slate-200 flex flex-col h-full"
    >
      <div className="h-16 border-b border-slate-200 flex items-center justify-between px-5 bg-white shrink-0">
        <div className="font-bold text-slate-800 flex items-center gap-2">
          Care Delivery AI
          <button
            type="button"
            onClick={() => beginRename(tabs.activeTabId)}
            aria-label="Rename conversation"
            className="text-slate-400 hover:text-slate-600 text-xs"
          >
            ✎
          </button>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label="Collapse Care Delivery AI"
            title="Collapse Care Delivery AI"
            className="text-slate-400 hover:text-slate-600 text-lg"
          >
            ▶
          </button>
        </div>
      </div>

      <ThreadMenu
        tabs={tabs.tabs}
        activeTabId={tabs.activeTabId}
        onSelect={tabs.selectTab}
        onNew={tabs.newTab}
        onRename={beginRename}
        onDelete={handleDelete}
      />

      {renamingTabId !== null && (
        <div className="px-5 py-2 border-b border-slate-200 flex gap-2">
          <input
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitRename(renamingTabId);
              }
            }}
            aria-label="New conversation title"
            className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm"
          />
          <button
            type="button"
            onClick={() => commitRename(renamingTabId)}
            className="px-2 py-1 bg-slate-800 text-white rounded text-xs"
          >
            Save
          </button>
        </div>
      )}

      <MessageList
        messages={activeTab?.messages ?? []}
        streaming={tabs.streaming}
        streamedText={tabs.streamedText}
        pendingRunId={pendingRunId}
        onResolved={refresh}
        sourceTitles={activeSourceTitles}
      />

      <Composer disabled={tabs.streaming} onSend={handleSend} />
    </aside>
  );
};

export default CareDeliveryChat;
